/**
 * atelier-core.js — the runtime of the painted portfolio.
 *
 * One small shared engine for every atelier component:
 *   - one requestAnimationFrame scheduler (parks on hidden tabs)
 *   - motion tiers: 'full' | 'reduced' (from prefers-reduced-motion),
 *     written to <html data-atl-motion> so CSS can gate animation
 *   - a shared IntersectionObserver for one-shot reveals
 *   - a mount system: an element declares data-atelier="name" and a
 *     module registers a factory; the factory may return a cleanup
 *     function, which runs on Shopify's section unload so the Theme
 *     Editor never leaks loops or listeners
 *   - tiny helpers (clamp, lerp, hash, frame-coalesced handlers)
 *
 * No concept lives here — only machinery.
 */
(() => {
  if (window.Atelier && window.Atelier.__ready) return;

  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  /* Deterministic 0..1 hash — same seed, same painting, every visit. */
  const hash = (n) => {
    const s = Math.sin(n * 127.1 + 311.7) * 43758.5453;
    return s - Math.floor(s);
  };

  /* ---- Motion tiers ---- */
  const prm = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motion = {
    get tier() {
      return prm.matches ? 'reduced' : 'full';
    },
    allows() {
      return !prm.matches;
    },
    onChange(fn) {
      const h = () => {
        sync();
        fn(motion.tier);
      };
      prm.addEventListener('change', h);
      return () => prm.removeEventListener('change', h);
    },
  };
  const sync = () => {
    document.documentElement.setAttribute('data-atl-motion', motion.tier);
  };
  sync();
  prm.addEventListener('change', sync);

  /* ---- Scheduler: one rAF, many tasks ---- */
  const tasks = new Set();
  let rafId = null;
  let last = 0;
  const tick = (now) => {
    rafId = null;
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now;
    tasks.forEach((fn) => {
      try {
        fn(dt, now);
      } catch (err) {
        tasks.delete(fn);
        console.error('[atelier] task removed after error', err);
      }
    });
    if (tasks.size) rafId = window.requestAnimationFrame(tick);
  };
  const scheduler = {
    add(fn) {
      tasks.add(fn);
      if (!rafId && !document.hidden) {
        last = performance.now();
        rafId = window.requestAnimationFrame(tick);
      }
      return () => tasks.delete(fn);
    },
  };
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    } else if (tasks.size && !rafId) {
      last = performance.now();
      rafId = window.requestAnimationFrame(tick);
    }
  });

  /* ---- One-shot reveal observer ---- */
  let revealObs = null;
  const observeReveal = (el) => {
    if (!('IntersectionObserver' in window) || !motion.allows()) {
      el.classList.add('is-revealed');
      return;
    }
    if (!revealObs) {
      revealObs = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add('is-revealed');
              revealObs.unobserve(e.target);
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.06 }
      );
    }
    revealObs.observe(el);
  };
  const revealAll = (root) => {
    (root || document).querySelectorAll('[data-atl-reveal]').forEach(observeReveal);
  };

  /* ---- Mount system ---- */
  const factories = new Map();
  const cleanups = new Map(); // element -> cleanup fn
  const mount = (el) => {
    const name = el.dataset.atelier;
    if (!name || cleanups.has(el)) return;
    const factory = factories.get(name);
    if (!factory) return;
    try {
      const cleanup = factory(el);
      cleanups.set(el, typeof cleanup === 'function' ? cleanup : () => {});
    } catch (err) {
      console.error(`[atelier] mount "${name}" failed`, err);
    }
  };
  const scan = (root) => {
    const r = root || document;
    if (r.matches && r.matches('[data-atelier]')) mount(r);
    r.querySelectorAll('[data-atelier]').forEach(mount);
    revealAll(r);
  };
  const register = (name, factory) => {
    factories.set(name, factory);
    document.querySelectorAll(`[data-atelier="${name}"]`).forEach(mount);
  };

  /* Theme Editor lifecycle: tear down cleanly, remount fresh. */
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-atelier]').forEach((el) => {
      const fn = cleanups.get(el);
      if (fn) {
        try {
          fn();
        } catch (err) {
          /* already gone */
        }
        cleanups.delete(el);
      }
    });
  });
  document.addEventListener('shopify:section:load', (e) => scan(e.target));

  /* ---- Helpers ---- */
  const helpers = {
    clamp,
    lerp,
    hash,
    /* Coalesce bursty events into one call per frame. */
    onFrame(fn) {
      let queued = false;
      return (...args) => {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(() => {
          queued = false;
          fn(...args);
        });
      };
    },
    /* Size a canvas to its element with a DPR cap. */
    fitCanvas(canvas, ctx, cap) {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, cap || 2);
      const w = Math.max(1, Math.round(rect.width));
      const h = Math.max(1, Math.round(rect.height));
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return { w, h, dpr };
    },
    /* Screen-reader announcements via one polite live region. */
    announce(message) {
      let region = document.getElementById('atl-live');
      if (!region) {
        region = document.createElement('p');
        region.id = 'atl-live';
        region.className = 'atl-visually-hidden';
        region.setAttribute('role', 'status');
        region.setAttribute('aria-live', 'polite');
        document.body.appendChild(region);
      }
      region.textContent = '';
      window.setTimeout(() => {
        region.textContent = message;
      }, 60);
    },
  };

  window.Atelier = { __ready: true, motion, scheduler, register, scan, revealAll, helpers };
  document.dispatchEvent(new CustomEvent('atelier:ready', { detail: window.Atelier }));
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  } else {
    scan(document);
  }
})();
